import { useContext, useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import { TextField } from "@mui/material";

import CloseIcon from "assets/icons/close.svg?react";
import Button from "components/Button";
import HollowButton from "components/HollowButton";

import ModalsManagerContext from "contexts/ModalsManager";
import parcelService from "api/parcelService";
import { IPackage } from "dto/IPackage";
import { ENotificationType, notification } from "utils/notification";

import styles from "./styles.module.scss";

interface DispatchRef {
  GroupId: string | null;
  Title: string | null;
  Signature: string | null;
}

const toRef = (raw: string | null): DispatchRef | null => {
  if (!raw) return null;
  const pack = JSON.parse(raw) as IPackage;
  return {
    GroupId: pack.S3 ?? null,
    Title: pack.S1 ?? null,
    Signature: pack.ParcelNumber ?? pack.S4 ?? null,
  };
};

const DispatchModal = () => {
  const { modals, setModals } = useContext(ModalsManagerContext);
  const queryClient = useQueryClient();

  const ref = useMemo<DispatchRef | null>(
    () => toRef(localStorage.getItem("package")),
    [],
  );

  const { mutate: dispatchParcel } = parcelService.useDispatchParcel();

  const [dispatchNumber, setDispatchNumber] = useState("");
  // data lokalna (YYYY-MM-DD) — unikamy przesunięcia o dzień z toISOString (UTC)
  const [dispatchDate, setDispatchDate] = useState(
    new Date().toLocaleDateString("sv-SE"),
  );
  const [carrier, setCarrier] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const close = () => setModals({ ...modals, dispatch: false });

  const submit = () => {
    if (!ref?.GroupId) {
      setError("Brak identyfikatora grupy pism.");
      return;
    }
    setError("");
    setIsLoading(true);
    dispatchParcel(
      {
        GroupId: ref.GroupId,
        DispatchNumber: dispatchNumber || null,
        DispatchDate: dispatchDate ? new Date(dispatchDate).toISOString() : null,
        Carrier: carrier || null,
      },
      {
        onSuccess: () => {
          setIsLoading(false);
          queryClient.invalidateQueries("packages");
          notification({
            type: ENotificationType.SUCCESS,
            title: "Nadano",
            text: "Przesyłki papierowe oznaczono jako nadane, elektroniczne przekazano do wysyłki.",
          });
          close();
        },
        onError: () => {
          setIsLoading(false);
          notification({
            type: ENotificationType.ERROR,
            title: "Błąd",
            text: "Nie udało się zarejestrować nadania.",
          });
        },
      },
    );
  };

  return (
    <div className={styles.overlay} onMouseDown={close}>
      <div className={styles.modal} onMouseDown={(e) => e.stopPropagation()}>
        <div className={styles.title}>
          <div>
            Nadanie / wysyłka
            {ref?.Title && <div className={styles.subtitle}>{ref.Title}</div>}
            {ref?.Signature && (
              <div className={styles.signature}>{ref.Signature}</div>
            )}
          </div>
          <CloseIcon className={styles.closeIcon} onClick={close} />
        </div>

        <div className={styles.content}>
          <div className={styles.hint}>
            Dane nadania dotyczą przesyłek papierowych. Przesyłki elektroniczne
            (e-mail / e-Doręczenia) z tej wysyłki zostaną automatycznie przekazane do
            wysłania.
          </div>

          <TextField
            size="small"
            label="Numer nadawczy (opcjonalnie)"
            value={dispatchNumber}
            onChange={(e) => setDispatchNumber(e.target.value)}
            fullWidth
          />

          <div className={styles.row}>
            <div className={styles.field}>
              <label className={styles.label}>Data nadania</label>
              <input
                type="date"
                className={styles.dateInput}
                value={dispatchDate}
                onChange={(e) => setDispatchDate(e.target.value)}
              />
            </div>
            <TextField
              size="small"
              label="Przewoźnik / operator (opcjonalnie)"
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
              fullWidth
            />
          </div>

          {error && <div className={styles.error}>{error}</div>}
        </div>

        <div className={styles.footer}>
          <HollowButton text="Anuluj" onClick={close} />
          <Button text="Nadaj / wyślij" onClick={submit} loading={isLoading} />
        </div>
      </div>
    </div>
  );
};

export default DispatchModal;
